import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import FadeContent from '../components/FadeContent';
import Counter from '../components/Counter';
import ShareButtons from '../components/ShareButtons';
import { fetchCount, sendHeartbeat, submitLead } from '../lib/api';

const REFERRAL_OPTIONS = [
  'Instagram',
  'TikTok',
  'Friend / Word of Mouth',
  'Event Flyer',
  'Google Search',
  'Other',
];

// Fake bearers — cycled on the hero card for ambient social proof.
// Mix reflective of Hayward, CA demographics (Latino, Filipino, Black, mixed).
const BEARER_NAMES = [
  'Carlos M.',
  'Aaliyah J.',
  'Jasmine G.',
  'Marcus R.',
  'Sofia D.',
  'Devon W.',
  'Isabella T.',
  'Jordan P.',
  'Diego H.',
  'Mei L.',
  'Tony N.',
  'Brianna S.',
  'Kayla F.',
  'Rashad B.',
  'Camila V.',
  'Andre C.',
  'Leilani O.',
  'Marisol R.',
  'Jamal K.',
  'Priya S.',
];

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  referralSource: '',
  smsEmailConsent: false,
};

const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRx = /^[\d\s+().-]{7,20}$/;

export default function Landing() {
  const [serverCount, setServerCount] = useState(247);
  const [displayCount, setDisplayCount] = useState(247);
  const [passNumber, setPassNumber] = useState(null);
  const [bearerIdx, setBearerIdx] = useState(0);
  const [prevBearerIdx, setPrevBearerIdx] = useState(-1);
  const [exiting, setExiting] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState('');

  const formSectionRef = useRef(null);
  const cardRef = useRef(null);
  const heroRef = useRef(null);
  const hookLayerRef = useRef(null);
  const cardLayerRef = useRef(null);

  // Initial count
  useEffect(() => {
    fetchCount()
      .then((c) => {
        setServerCount(c);
        setDisplayCount(c);
      })
      .catch(() => {});
  }, []);

  // ===== Active-visitor heartbeat — the ONLY thing that bumps the counter.
  // We poll while the tab is visible. The backend rate-limits to one bump
  // every 8-25s no matter how many visitors are pinging, so the counter only
  // climbs when real users are on the site. =====
  useEffect(() => {
    if (success) return;
    let cancelled = false;

    const ping = async () => {
      if (document.hidden) return;
      try {
        const c = await sendHeartbeat();
        if (cancelled) return;
        setServerCount(c);
        setDisplayCount((cur) => (c > cur ? c : cur));
      } catch {
        /* swallow — keep the UI optimistic */
      }
    };

    // Quick first ping (2-5s) so visitors see live activity early,
    // then steady polling.
    const initial = setTimeout(ping, 2000 + Math.random() * 3000);
    const interval = setInterval(ping, 5000);
    const onVisibility = () => {
      if (!document.hidden) ping();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearTimeout(initial);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [success]);

  // ===== Cinematic intro: hook -> hook out -> card+reserve in =====
  useEffect(() => {
    const hook = hookLayerRef.current;
    const card = cardLayerRef.current;
    if (!hook || !card) return;

    gsap.set(hook, { autoAlpha: 0, filter: 'blur(14px)', y: 0 });
    gsap.set(card, { autoAlpha: 0, filter: 'blur(16px)', y: 48 });

    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
    tl.to(
      hook,
      {
        autoAlpha: 1,
        filter: 'blur(0px)',
        duration: 1.8,
      },
      0.6
    )
      .to(
        hook,
        {
          autoAlpha: 0,
          filter: 'blur(14px)',
          y: -8,
          duration: 1.3,
          ease: 'power2.inOut',
        },
        3.6
      )
      .to(
        card,
        {
          autoAlpha: 1,
          filter: 'blur(0px)',
          y: 0,
          duration: 1.8,
          ease: 'power3.out',
        },
        4.0
      );

    return () => tl.kill();
  }, []);

  // ===== Cycle bearer names on hero card =====
  useEffect(() => {
    const id = setInterval(() => {
      setBearerIdx((i) => {
        setPrevBearerIdx(i);
        return (i + 1) % BEARER_NAMES.length;
      });
    }, 2800);
    return () => clearInterval(id);
  }, []);

  // ===== Card hover/parallax tilt — swings the lanyard rig =====
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const rig = el.parentElement; // .hp-lanyard wrapper
    const handleMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      gsap.to(el, {
        rotateY: x * 14,
        rotateX: -y * 10,
        duration: 0.6,
        ease: 'power3.out',
        transformPerspective: 1000,
      });
      if (rig) {
        gsap.to(rig, {
          rotateZ: x * 4,
          duration: 0.9,
          ease: 'power3.out',
        });
      }
    };
    const handleLeave = () => {
      gsap.to(el, { rotateY: 0, rotateX: 0, duration: 0.8, ease: 'power3.out' });
      if (rig) {
        // small recovery swing
        gsap.to(rig, {
          rotateZ: 0,
          duration: 2.2,
          ease: 'elastic.out(1, 0.35)',
        });
      }
    };
    el.addEventListener('mousemove', handleMove);
    el.addEventListener('mouseleave', handleLeave);
    return () => {
      el.removeEventListener('mousemove', handleMove);
      el.removeEventListener('mouseleave', handleLeave);
    };
  }, []);

  // Keep display in sync if server count jumps ahead (e.g. real submission)
  useEffect(() => {
    setDisplayCount((c) => (serverCount > c ? serverCount : c));
  }, [serverCount]);

  // ===== Smooth scroll to form (custom easing, no jank) =====
  const scrollToForm = () => {
    const target = formSectionRef.current;
    if (!target) return;
    const targetY = target.getBoundingClientRect().top + window.scrollY;
    const startY = window.scrollY;
    const dist = targetY - startY;
    if (Math.abs(dist) < 4) return;
    const duration = Math.min(1400, 700 + Math.abs(dist) * 0.6);
    const startT = performance.now();
    const easeInOut = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const tick = (now) => {
      const t = Math.min(1, (now - startT) / duration);
      window.scrollTo(0, startY + dist * easeInOut(t));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    let triggered = false;
    const onWheel = (e) => {
      if (triggered) return;
      if (e.deltaY > 12) {
        triggered = true;
        scrollToForm();
      }
    };
    let touchStartY = 0;
    const onTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };
    const onTouchMove = (e) => {
      if (triggered) return;
      const dy = touchStartY - e.touches[0].clientY;
      if (dy > 30) {
        triggered = true;
        scrollToForm();
      }
    };
    hero.addEventListener('wheel', onWheel, { passive: true });
    hero.addEventListener('touchstart', onTouchStart, { passive: true });
    hero.addEventListener('touchmove', onTouchMove, { passive: true });
    return () => {
      hero.removeEventListener('wheel', onWheel);
      hero.removeEventListener('touchstart', onTouchStart);
      hero.removeEventListener('touchmove', onTouchMove);
    };
  }, []);

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim()) e.lastName = 'Required';
    if (!emailRx.test(form.email)) e.email = 'Enter a valid email';
    if (!phoneRx.test(form.phone)) e.phone = 'Enter a valid phone';
    if (!form.smsEmailConsent) e.smsEmailConsent = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setServerError('');
    if (!validate() || submitting) return;
    setSubmitting(true);
    try {
      const res = await submitLead(form);
      // Land the counter on the user's pass number so it matches their badge
      setServerCount(res.count);
      setDisplayCount(res.count);
      setPassNumber(res.count);
      // Smoothly fade out the form, then mount the success state
      setExiting(true);
      setTimeout(() => {
        setSuccess(true);
        setExiting(false);
      }, 420);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Something went wrong. Try again.';
      setServerError(typeof msg === 'string' ? msg : 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  return (
    <div className="hp-root">
      {/* ===== HERO / INTRO ===== */}
      <section ref={heroRef} className="hp-section hp-hero">
        <div className="hp-noise" aria-hidden="true" />

        <div className="hp-stage">
          {/* Layer 1 — Hook */}
          <div
            ref={hookLayerRef}
            className="hp-stage-layer hp-hook-layer"
            data-testid="intro-hook-layer"
          >
            <p className="hp-eyebrow" data-testid="intro-eyebrow">
              <span className="hp-dot" />
              The Hall Pass · Hayward, CA
            </p>
            <h1 className="hp-hook" data-testid="intro-hook">
              Do you have your<br />
              <em>Hall Pass?</em>
            </h1>
          </div>

          {/* Layer 2 — Vertical card + scroll cue */}
          <div
            ref={cardLayerRef}
            className="hp-stage-layer hp-card-layer"
            data-testid="card-layer"
          >
            <h2 className="hp-reserve-headline" data-testid="reserve-headline">
              Reserve yours <em>now</em>.
            </h2>

            <div className="hp-lanyard" data-testid="lanyard">
              <div className="hp-lanyard-strap" aria-hidden="true">
                <div className="hp-lanyard-stripe" />
                <div className="hp-lanyard-stripe hp-lanyard-stripe-2" />
                <span className="hp-lanyard-text">THE HALL PASS · THE HALL PASS · THE HALL PASS</span>
              </div>
              <div className="hp-lanyard-clip" aria-hidden="true">
                <div className="hp-lanyard-clip-ring" />
                <div className="hp-lanyard-clip-body" />
              </div>

              <button
                ref={cardRef}
                type="button"
                className="hp-card"
                onClick={scrollToForm}
                data-testid="vip-card"
                aria-label="Reserve your Hall Pass"
              >
                <div className="hp-card-inner">
                  <div className="hp-card-perfs hp-card-perfs-top" aria-hidden="true">
                    <span /><span /><span /><span /><span /><span /><span />
                  </div>

                  <div className="hp-card-head">
                    <span className="hp-card-stamp">MEMBERS · ONLY</span>
                    <span className="hp-card-no">№ 001</span>
                  </div>

                  <img
                    src="/assets/hall-pass-logo-transparent.png"
                    alt="The Hall Pass"
                    className="hp-card-logo"
                    draggable="false"
                  />

                  <div className="hp-card-divider" aria-hidden="true" />

                  <div className="hp-card-meta">
                    <div className="hp-card-meta-row">
                      <span className="hp-card-label">Bearer</span>
                      <span className="hp-card-bearer-wrap" data-testid="card-bearer">
                        {prevBearerIdx >= 0 && (
                          <span
                            key={`out-${prevBearerIdx}`}
                            className="hp-card-bearer hp-card-bearer-out"
                          >
                            {BEARER_NAMES[prevBearerIdx]}
                          </span>
                        )}
                        <span
                          key={`in-${bearerIdx}`}
                          className="hp-card-bearer hp-card-bearer-in"
                        >
                          {BEARER_NAMES[bearerIdx]}
                        </span>
                      </span>
                    </div>
                    <div className="hp-card-meta-row">
                      <span className="hp-card-label">Issued</span>
                      <span className="hp-card-value">Hayward, CA</span>
                    </div>
                    <div className="hp-card-meta-row">
                      <span className="hp-card-label">Status</span>
                      <span className="hp-card-value hp-card-status">Pending</span>
                    </div>
                  </div>

                  <div className="hp-card-foot">
                    <span>The Hall Pass</span>
                    <span>Coming Soon</span>
                  </div>

                  <div className="hp-card-shine" aria-hidden="true" />
                </div>
              </button>
            </div>

            <button
              type="button"
              onClick={scrollToForm}
              className="hp-scroll-btn"
              data-testid="scroll-cue"
            >
              <span>Tap or scroll to enter</span>
              <svg width="12" height="20" viewBox="0 0 12 20" fill="none" aria-hidden="true">
                <path d="M6 1V19M6 19L1 14M6 19L11 14" stroke="currentColor" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* ===== RESERVATION ===== */}
      <section
        ref={formSectionRef}
        className="hp-section hp-reserve"
        data-testid="reservation-section"
      >
        <div className="hp-noise" aria-hidden="true" />
        <div className="hp-reserve-inner">
          <FadeContent blur duration={1200} className="hp-reserve-head">
            <p className="hp-eyebrow">
              <span className="hp-dot" />
              Reservation · Coming Soon
            </p>
            <h2 className="hp-h2" data-testid="reserve-heading">
              Reserve your spot today.
            </h2>
            <p className="hp-sub" data-testid="reserve-sub">
              Be first to know when The Hall Pass opens in Hayward.
            </p>
          </FadeContent>

          <FadeContent blur duration={1200} delay={200} className="hp-counter-wrap">
            <div className="hp-counter-line">
              <div className="hp-counter-rule" />
              <div className="hp-counter-block" data-testid="hall-pass-counter">
                <Counter
                  value={displayCount}
                  places={displayCount >= 1000 ? [1000, 100, 10, 1] : [100, 10, 1]}
                  fontSize={64}
                  fontWeight={300}
                  textColor="#fff"
                  gap={2}
                  digitPlaceHolders={false}
                />
              </div>
              <div className="hp-counter-rule" />
            </div>
            <div className="hp-counter-label">
              <span className="hp-live-dot" /> Hall Passes Reserved · live
            </div>
          </FadeContent>

          {!success ? (
            <FadeContent blur duration={1000} delay={300} className="hp-form-wrap">
              <form
                onSubmit={handleSubmit}
                noValidate
                className={`hp-form ${exiting ? 'hp-form-exit' : ''}`}
                data-testid="reservation-form"
              >
                <div className="hp-row">
                  <FormField
                    label="First Name"
                    value={form.firstName}
                    onChange={(v) => updateField('firstName', v)}
                    error={errors.firstName}
                    testId="input-first-name"
                    autoComplete="given-name"
                  />
                  <FormField
                    label="Last Name"
                    value={form.lastName}
                    onChange={(v) => updateField('lastName', v)}
                    error={errors.lastName}
                    testId="input-last-name"
                    autoComplete="family-name"
                  />
                </div>

                <FormField
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(v) => updateField('email', v)}
                  error={errors.email}
                  testId="input-email"
                  autoComplete="email"
                />
                <FormField
                  label="Phone"
                  type="tel"
                  value={form.phone}
                  onChange={(v) => updateField('phone', v)}
                  error={errors.phone}
                  testId="input-phone"
                  autoComplete="tel"
                  placeholder="(555) 555-5555"
                />

                <div className="hp-field">
                  <label className="hp-label">
                    How did you hear about us?
                    <span className="hp-optional"> (optional)</span>
                  </label>
                  <div className="hp-select-wrap">
                    <select
                      value={form.referralSource}
                      onChange={(e) => updateField('referralSource', e.target.value)}
                      className="hp-select"
                      data-testid="select-referral"
                    >
                      <option value="">Select an option</option>
                      {REFERRAL_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                    <svg className="hp-select-icon" width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                      <path d="M1 1L5 5L9 1" stroke="currentColor" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>

                <label className="hp-consent" data-testid="consent-row">
                  <input
                    type="checkbox"
                    checked={form.smsEmailConsent}
                    onChange={(e) => updateField('smsEmailConsent', e.target.checked)}
                    data-testid="checkbox-consent"
                    className="hp-checkbox"
                  />
                  <span className="hp-consent-text">
                    I agree to receive email and SMS updates from The Hall Pass.
                  </span>
                </label>
                {errors.smsEmailConsent && (
                  <p className="hp-err" data-testid="consent-error">
                    {errors.smsEmailConsent}
                  </p>
                )}

                {serverError && (
                  <p className="hp-err hp-err-server" data-testid="server-error">
                    {serverError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="hp-cta"
                  data-testid="submit-reservation"
                >
                  {submitting ? (
                    <span className="hp-cta-loading">
                      <span className="hp-spinner" />
                      Reserving…
                    </span>
                  ) : (
                    'Reserve My Hall Pass'
                  )}
                </button>

                <p className="hp-disclaimer" data-testid="sms-disclaimer">
                  By submitting this form, you agree to receive emails and text messages
                  from The Hall Pass about launch updates, reservations, promotions, and
                  related announcements. Message and data rates may apply. Message
                  frequency may vary. Reply STOP to unsubscribe or HELP for help. Consent
                  is not a condition of purchase. You may receive a confirmation text to
                  complete your opt-in.
                </p>
                <p className="hp-disclaimer hp-disclaimer-legal" data-testid="legal-links">
                  By submitting, you also agree to our{' '}
                  <Link to="/terms" className="hp-disclaimer-link">Terms of Service</Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="hp-disclaimer-link">Privacy Policy</Link>.
                </p>
              </form>
            </FadeContent>
          ) : (
            <div className="hp-success" data-testid="success-state">
              <div className="hp-success-mark" aria-hidden="true">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <path d="M7 18L15 26L29 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="hp-success-title">You&apos;re on the list.</h3>
              {passNumber !== null && (
                <div className="hp-pass-no" data-testid="success-pass-number">
                  <span className="hp-pass-no-label">Hall Pass</span>
                  <span className="hp-pass-no-value">№ {passNumber}</span>
                </div>
              )}
              <p className="hp-success-sub">Your Hall Pass is pending.</p>
              <p className="hp-success-foot">
                Keep your phone close. We&apos;ll send updates soon.
              </p>
              <ShareButtons passNumber={passNumber} />
            </div>
          )}
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="hp-footer" data-testid="footer">
        <div className="hp-footer-inner">
          <span className="hp-footer-mark">THP</span>
          <span className="hp-footer-line">The Hall Pass — Hayward, CA</span>
          <span className="hp-footer-meta">Coming Soon</span>
        </div>
        <div className="hp-footer-legal" data-testid="footer-legal">
          <Link to="/privacy" className="hp-footer-link" data-testid="footer-privacy">
            Privacy Policy
          </Link>
          <span className="hp-footer-sep" aria-hidden="true">·</span>
          <Link to="/terms" className="hp-footer-link" data-testid="footer-terms">
            Terms of Service
          </Link>
          <span className="hp-footer-sep" aria-hidden="true">·</span>
          <span className="hp-footer-copy">
            © {new Date().getFullYear()} The Hall Pass. All rights reserved.
          </span>
        </div>
      </footer>
    </div>
  );
}

function FormField({
  label,
  value,
  onChange,
  error,
  type = 'text',
  testId,
  autoComplete,
  placeholder,
}) {
  return (
    <div className="hp-field">
      <label className="hp-label">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`hp-input ${error ? 'hp-input-err' : ''}`}
        data-testid={testId}
        autoComplete={autoComplete}
        placeholder={placeholder}
      />
      {error && <p className="hp-err">{error}</p>}
    </div>
  );
}
